import React, { useMemo } from 'react';

import cs from 'classnames';
import { AgGridReact } from 'ag-grid-react';

import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';

import TableFilters from './TableFilters';
import TablePagination from './TablePagination';

const NullOverlay = () => null;

const EmptyServicesState = () => (
  <div className="flex h-full w-full flex-col items-center justify-center py-12">
    <Typography weight="semibold" className="mb-1 text-neutral-800">
      No services found
    </Typography>
    <Typography className="text-neutral-500">
      Try adjusting your filters or specialty selection.
    </Typography>
  </div>
);

const ErrorServicesState = ({ onRetry }) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-2 py-12">
    <Typography weight="semibold" className="text-neutral-800">
      Couldn&apos;t load services
    </Typography>
    <Typography className="text-neutral-500">
      Something went wrong while loading services. Please try again.
    </Typography>
    <Button text="Retry" variant="tertiary" size="small" onClick={onRetry} />
  </div>
);

const ServicesTable = ({
  columnDefs,
  tabsData,
  selectedSpecialty,
  onSpecialtyChange,
  filters,
  onFilterChange,
  showControls,
  onColumnChange,
  onColumnDragEnd,
  paginatedAndFilteredData,
  onSelectionChanged,
  page,
  limit,
  totalItems,
  onPageChange,
  onLimitChange,
  onRemove,
  onEditRow,
  isLoading,
  isError,
  onRetry
}) => {
  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true
    }),
    []
  );

  const gridContext = useMemo(() => ({ onRemove, onEditRow }), [onRemove, onEditRow]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-solid border-neutral-200">
      <div className="flex-shrink-0 border-0 border-b border-solid border-neutral-200 bg-white px-3 py-2">
        <ToggleSwitch
          items={tabsData}
          active={selectedSpecialty}
          onClick={(item) => onSpecialtyChange(item.value)}
        />
      </div>

      {showControls && !isError && (
        <div className="flex-shrink-0">
          <TableFilters
            filters={filters}
            changeFilters={onFilterChange}
            columnDefs={columnDefs}
            onColumnChange={onColumnChange}
            onColumnDragEnd={onColumnDragEnd}
          />
        </div>
      )}

      <div
        className={cs('ag-theme-quartz min-h-0 flex-1 overflow-auto', isLoading && 'opacity-50')}>
        {isError ? (
          <ErrorServicesState onRetry={onRetry} />
        ) : (
          <AgGridReact
            columnDefs={columnDefs}
            rowData={paginatedAndFilteredData}
            defaultColDef={defaultColDef}
            context={gridContext}
            onSelectionChanged={onSelectionChanged}
            getRowId={(params) => params.data.code}
            rowSelection="multiple"
            noRowsOverlayComponent={isLoading ? NullOverlay : EmptyServicesState}
            suppressRowClickSelection={true}
            domLayout="autoHeight"
          />
        )}
      </div>

      {showControls && !isError && (
        <div className="flex-shrink-0">
          <TablePagination
            page={page}
            limit={limit}
            totalItems={totalItems}
            setLimit={onLimitChange}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ServicesTable;
